/**
 * MPI (ManagerTable Pressure Index) — single source of truth for tier logic.
 *
 * | Status           | Range  | Color   | Emoji |
 * |------------------|--------|---------|-------|
 * | Stable           | 0–29   | Green   | ✅    |
 * | Mild Pressure    | 30–49  | Yellow  | ⚠️    |
 * | Under Pressure   | 50–69  | Orange  | 🔥    |
 * | Critical         | 70–100 | Red     | 🚨    |
 */

export interface MpiTierInfo {
  label: string;
  emoji: string;
  /** Hex colour for inline styles / SVG (brand tier colour) */
  color: string;
  /** Hex fill that passes 4.5:1 with white text (use for chips/badges) */
  chipBg: string;
  /** Foreground to pair with chipBg */
  chipFg: string;
  /** Accessible Tailwind text class for light + dark themes */
  textClass: string;
  /** Tailwind text-* class */
  tailwindText: string;
  /** Tailwind bg-* class */
  tailwindBg: string;
  /** Tailwind bg/border combo for card borders */
  tailwindBorder: string;
  /** Badge variant for shadcn Badge */
  variant: "destructive" | "secondary" | "outline";
}

const CRITICAL: MpiTierInfo = {
  label: "Critical",
  emoji: "🚨",
  color: "#ef4444",
  chipBg: "#b91c1c",
  chipFg: "#ffffff",
  textClass: "text-red-700 dark:text-red-400",
  tailwindText: "text-red-500",
  tailwindBg: "bg-red-500",
  tailwindBorder: "bg-red-500/10 border-red-500/30",
  variant: "destructive",
};

const HOT: MpiTierInfo = {
  label: "Under Pressure",
  emoji: "🔥",
  color: "#f97316",
  chipBg: "#c2410c",
  chipFg: "#ffffff",
  textClass: "text-orange-700 dark:text-orange-400",
  tailwindText: "text-orange-500",
  tailwindBg: "bg-orange-500",
  tailwindBorder: "bg-orange-500/10 border-orange-500/30",
  variant: "secondary",
};

const WARM: MpiTierInfo = {
  label: "Mild Pressure",
  emoji: "⚠️",
  color: "#eab308",
  chipBg: "#a16207",
  chipFg: "#ffffff",
  textClass: "text-yellow-700 dark:text-yellow-400",
  tailwindText: "text-yellow-500",
  tailwindBg: "bg-yellow-500",
  tailwindBorder: "bg-yellow-500/10 border-yellow-500/30",
  variant: "outline",
};

const COOL: MpiTierInfo = {
  label: "Stable",
  emoji: "✅",
  color: "#22c55e",
  chipBg: "#15803d",
  chipFg: "#ffffff",
  textClass: "text-green-700 dark:text-green-400",
  tailwindText: "text-green-500",
  tailwindBg: "bg-green-500",
  tailwindBorder: "bg-green-500/10 border-green-500/30",
  variant: "outline",
};


/** Takes a score on the 0–100 scale */
export function getMpiTier(score: number): MpiTierInfo {
  if (score >= 70) return CRITICAL;
  if (score >= 50) return HOT;
  if (score >= 30) return WARM;
  return COOL;
}

/** Takes a score on the 0–1 decimal scale (multiplies by 100 internally) */
export function getMpiTierDecimal(decimal: number): MpiTierInfo {
  return getMpiTier(decimal * 100);
}

/** Returns just the hex colour for a 0–100 score */
export function getMpiColor(score: number): string {
  return getMpiTier(score).color;
}

/** Returns just the hex colour for a 0–1 score */
export function getMpiColorDecimal(decimal: number): string {
  return getMpiTier(decimal * 100).color;
}

/** Critical zone threshold (used for reference lines in charts) */
export const MPI_CRITICAL_THRESHOLD = 70;
